import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import * as Enums from "@/utils/enums";
import { IconTrash } from "@tabler/icons";
import SectionTable from "@/PageComponents/SectionTable/SectionTable";
import { IconPlus } from "@tabler/icons-react";
import { Anchor, Box, Button, Flex, Menu } from "@mantine/core";
import { SimpleColumnMapping } from "@/PageComponents/SimpleTable/SimpleTable";
import permissionService from "@/services/permission/permission-service";
import helper from "@/utils/helper";
import { ColumnMappingData } from "@/PageComponents/Table/table-model";
import SCModal from "@/PageComponents/Modal/SCModal";
import ImageWithZoom from "@/PageComponents/Attachment/ImageWithZoom";
import { calculateLineTotalInclusive, calculateInclusive, calculateLineProfit, calculateMarkup } from '@/utils/line-totals';
import { useLineItems } from "@/hooks/useLineItems";
import { useStableCallback } from "@/hooks/useStableCallback";
import { fetchStockTotals } from "@/PageComponents/SectionTable/stock-totals";

const QuotesSectionTable: FC<
    {
        descriptionColumnWidth?: string
        quote: any;
        quoteItems: any[];
        itemId: string;
        module: number;
        onDataUpdate: (newData: any[], newSections: any[]) => void;
        addOptions: any[];
        onAddAction: (name: string) => void;
        onAddToSectionAction: (name: string, group: any) => void
        integration: boolean;
        companyTaxPercentage: number;
        onItemClicked: (item: any, index: number) => void
        onPredictedDefaultSectionPdfSettingsChanged?: (pdfSettings: { HideLineItems: boolean; DisplaySubtotal: boolean; }) => void
        onColumnMappingLoaded?: (mapping: SimpleColumnMapping[]) => void
        userColumnConfig?: ColumnMappingData[]
        customChildren?: ReactNode
    }> = (props: any) => {

        const canUpdate = useMemo(() => props.quote.QuoteStatus === Enums.QuoteStatus.Draft, [props.quote.QuoteStatus])

        const [hasManageCostingPermission] = useState(permissionService.hasPermission(Enums.PermissionName.ManageCosting));

        const [showImage, setShowImage] = useState<any>();

        const { items: currentData, savingItem, updateField, commit, removeItem, removeSection } = useLineItems(
            props.quoteItems,
            (items: any[]) => props.onDataUpdate(items, []),
            { costing: true }
        )

        const hasThumbnails = useMemo(() => currentData.some((x: any) => x.Inventory?.ThumbnailUrl), [currentData])

        const [stockQtyMap, setStockQtyMap] = useState<Record<string, number | null>>({})
        const fetchedStockIDsRef = useRef<Set<string>>(new Set())
        const mountedRef = useRef(true)
        useEffect(() => {
            mountedRef.current = true
            return () => { mountedRef.current = false }
        }, [])

        useEffect(() => {
            const newIDs = [...new Set(
                currentData
                    .filter((item: any) => item.InventoryID)
                    .map((item: any) => item.InventoryID as string)
            )].filter((id: string) => !fetchedStockIDsRef.current.has(id))

            if (newIDs.length === 0) return

            newIDs.forEach((id: string) => fetchedStockIDsRef.current.add(id))
            fetchStockTotals(newIDs).then((totals) => {
                if (mountedRef.current) setStockQtyMap(prev => ({ ...prev, ...totals }))
            })
        }, [currentData])

        const handleItemAction = useStableCallback((actionName: string, item: any, itemIndex: number, group: any) => {
            if (actionName === 'code') {
                props.onItemClicked(item, itemIndex)
            } else if (actionName === 'delete') {
                removeItem(item.ID)
            }
        })
        const handleSectionAction = useStableCallback((name: any, group: any) => {
            if (name === 'delete') {
                removeSection(group.id)
            } else {
                props.onAddToSectionAction(name, group)
            }
        })

        const calculateQuoteItemProfit = (quoteItem: any) => {
            // When cost is 0, Profit should equal LineTotalExclusive exactly.
            if (!quoteItem.Inventory || !quoteItem.Quantity || !quoteItem.UnitPriceExclusive) return "N/A";
            // No overall quote discount applied here
            return calculateLineProfit({
                quantity: quoteItem.Quantity,
                unitPrice: quoteItem.UnitPriceExclusive,
                costPrice: quoteItem.UnitCostPrice ?? quoteItem.Inventory?.CostPrice ?? 0,
                discountPercentage: quoteItem.LineDiscountPercentage,
            });
        }

        const mapping = useMemo(() => {
            return [
                ...(hasThumbnails ? [{
                    key: 'ImageUrl',
                    label: '',
                    valueFunction: (item: any) => item.Inventory?.ThumbnailUrl &&
                        <img
                            src={item.Inventory?.ThumbnailUrl}
                            style={{ height: 36, width: 36, borderRadius: 36, cursor: "pointer" }}
                            onClick={() => setShowImage(item)}
                        />
                }] : []),
                {
                    key: 'InventoryCode',
                    label: 'Code',
                    valueFunction: (item: any) => item.ProductID ? item.ProductNumber : item.InventoryCode ?? (canUpdate ? 'Edit' : ''),
                    linkAction: 'code'
                },
                {
                    key: 'Description',
                    keyFunction: (item: any) => item.InventoryID ? 'Description' : 'Description',
                    label: 'Description',
                    // type: canUpdate ? 'textInput' : undefined,
                    typeFunction: (item: any) => item.InventoryID ? 'textInput' : 'textArea',
                    stylingProps: {
                        miw: props.descriptionColumnWidth ?? '30vw',
                        darkerText: true
                    }
                },
                {
                    key: 'QuoteItemType',
                    label: 'Status',
                    type: 'status',
                    colorFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory ? (item.Integrated ? 'Green' : item.IntegrationMessage ? `Yellow` : undefined) : undefined),
                    valueFunction: (item: any) => (
                        item.QuoteItemType === Enums.QuoteItemType.Inventory && (item.Integrated ? 'Synced' : item.IntegrationMessage ? 'Error' : 'Not Synced'
                        ) || null),
                    showFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory),
                },
                {
                    key: 'Quantity',
                    label: 'Quantity',
                    typeFunction: (item: any) => item.QuoteItemType === Enums.QuoteItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    min: 0,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    alignRight: true,
                    showFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory)
                },
                {
                    key: 'StockQuantity',
                    label: 'Total Stock Qty',
                    alignRight: true,
                    showFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory),
                    valueFunction: (item: any) => {
                        if (!item.InventoryID) return null;
                        if (item.Inventory?.IsQuantityTracked === false) return '-';
                        const qty = stockQtyMap[item.InventoryID];
                        return (qty !== undefined && qty !== null) ? qty : '-';
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: false,
                    }
                },
                ...(hasManageCostingPermission ? [
                    {
                        key: 'UnitCostPrice',
                        label: 'Cost',
                        currencyValue: true,
                        customNumberProps: {
                            focusOnSelect: true
                        },
                        typeFunction: (item: any) => canUpdate && !!item.InventoryID ? 'numberInput' : undefined,
                        valueFunction: (item: any) => {
                            let costPrice = item.UnitCostPrice ?? +(item.Inventory?.CostPrice ?? 0);
                            return !!item.InventoryID ? costPrice : <></>;
                        },
                        columConfigOptions: {
                            allowShowToggle: true,
                            // allowReorder: true
                        },
                        // requiredFunction: item => item.QuoteItemType === Enums.QuoteItemType.Inventory
                    },
                    {
                        key: 'UnitPriceMarkup',
                        label: 'Markup %',
                        customNumberProps: {
                            focusOnSelect: true
                        },
                        required: false, // important to ensure clearing of values is possible without setting to 0
                        // min: -101,
                        alignRight: true,
                        typeFunction: (item: any) => canUpdate && !!item.InventoryID ? 'numberInput' : undefined,
                        valueFunction: (item: any) => {
                            if (item.QuoteItemType === Enums.QuoteItemType.Inventory) {
                                if (item.UnitCostPrice === 0) {
                                    return ''
                                } else if (typeof item.UnitPriceMarkup !== 'undefined') {
                                    return item.UnitPriceMarkup
                                } else {
                                    return calculateMarkup(item.UnitPriceExclusive, item.UnitCostPrice ?? +(item.Inventory?.CostPrice ?? 0));
                                }
                            } else {
                                return ''
                            }
                        },
                        columConfigOptions: {
                            allowShowToggle: true,
                            // allowReorder: true
                        }
                    }
                ] : []),
                {
                    key: 'UnitPriceExclusive',
                    label: 'Price Excl',
                    typeFunction: (item: any) => item.QuoteItemType === Enums.QuoteItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    // min: 0,
                    currencyValue: true,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    showFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory),
                    columConfigOptions: {
                        allowShowToggle: true,
                        disabled: true,
                        // allowReorder: true
                    },
                    // requiredFunction: item => item.QuoteItemType === Enums.QuoteItemType.Inventory
                },
                {
                    key: 'UnitPriceInclusive',
                    label: 'Price Incl',
                    typeFunction: (item: any) => item.QuoteItemType === Enums.QuoteItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    currencyValue: true,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    showFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory),
                    valueFunction: (item: any) => {
                        return calculateInclusive(item.UnitPriceExclusive, item.TaxPercentage);
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                        disabled: true,
                        defaultShown: false
                    },
                },
                {
                    key: 'LineDiscountPercentage',
                    label: 'Discount %',
                    typeFunction: (item: any) => item.QuoteItemType === Enums.QuoteItemType.Inventory ? (canUpdate ? 'numberInput' : undefined) : undefined,
                    customNumberProps: {
                        focusOnSelect: true
                    },
                    alignRight: true,
                    numberInputProps: {
                        min: 0,
                        max: 100
                    },
                    showFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory),
                    columConfigOptions: {
                        allowShowToggle: true,
                        // allowReorder: true
                    }
                },
                {
                    key: 'TaxPercentage',
                    label: 'Tax Rate',
                    typeFunction: (item: any) => item.QuoteItemType === Enums.QuoteItemType.Inventory ? (canUpdate ? 'selectInput' : undefined) : undefined,
                    showFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory),
                    selectOptions: [
                        {
                            label: 'Standard Rate',
                            value: props.companyTaxPercentage + ''
                        },
                        {
                            label: props.companyTaxPercentage === 0 ? 'Default (No VAT)' : 'No VAT',
                            value: 0 + ''
                        },
                    ].filter((x: any) => x.label !== 'Standard Rate' || props.companyTaxPercentage !== 0), // filter out standard rate if it is 0
                    alignRight: true,
                    columConfigOptions: {
                        allowShowToggle: true,
                        // allowReorder: true
                    }
                },
                ...(hasManageCostingPermission ? [
                    {
                        key: 'Profit',
                        label: 'Profit',
                        currencyValue: true,
                        // type: canUpdate ? 'numberInput' : undefined,
                        valueFunction: (item: any) => {
                            let profit = calculateQuoteItemProfit(item);
                            let profitCheck = +profit;
                            let notANumber = isNaN(profitCheck);
                            let colour = notANumber ? "white" : profitCheck > 0 ? "green" : "orange";
                            return <span style={{ color: colour }}> {notANumber ? "" : helper.getCurrencyValue(profit)}</span>
                        },
                        columConfigOptions: {
                            allowShowToggle: true,
                            // allowReorder: true
                        }
                    }
                ] : []),
                {
                    key: 'LineTotalExclusive',
                    label: 'Total Excl',
                    currencyValue: true,
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: true,
                        disabled: true,
                    },
                    // type: canUpdate ? 'numberInput' : undefined,
                    min: 0,
                    showFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory)
                },
                {
                    key: 'LineTotalInclusive',
                    label: 'Total Incl',
                    currencyValue: true,
                    min: 0,
                    showFunction: (item: any) => (item.QuoteItemType === Enums.QuoteItemType.Inventory),
                    valueFunction: (item: any) => {
                        if (!item || typeof item.LineTotalExclusive === 'undefined' || typeof item.TaxPercentage === 'undefined') {
                            return 0;
                        }

                        // Calculated from the unit price, not from the rounded exclusive line total -
                        // rebuilding it from that turns an entered R80.00 into R80.01.
                        return calculateLineTotalInclusive({
                            quantity: item.Quantity,
                            unitPrice: item.UnitPriceExclusive,
                            discountPercentage: item.LineDiscountPercentage,
                            taxPercentage: item.TaxPercentage,
                        });
                    },
                    columConfigOptions: {
                        allowShowToggle: true,
                        defaultShown: false,
                    }
                },
            ].filter((x: any) => x.key === 'QuoteItemType' ? props.integration : true) as SimpleColumnMapping[]
        }, [hasThumbnails, canUpdate, hasManageCostingPermission, props.companyTaxPercentage, props.descriptionColumnWidth, props.integration, stockQtyMap])

        useEffect(() => {
            if (mapping.length) {
                props.onColumnMappingLoaded && props.onColumnMappingLoaded(mapping)
            }
        }, [mapping]);

        const controls = useMemo(() => canUpdate ? [
                        {
                            name: 'delete',
                            // type: 'warning',
                            icon: <IconTrash />,
                            label: 'Remove',
                            activeLabel: 'Removing'
                        },
                    ] : [], [canUpdate])

        const sectionControls = useMemo(() => canUpdate ?
                            [
                                ...props.addOptions.map((x: any) => ({
                                    name: x.link,
                                    icon: IconPlus,
                                    label: x.text
                                })).filter((x: any) => x.name !== 'AddBundle'),
                                {
                                    name: 'ungroup'
                                },
                                {
                                    name: 'delete',
                                    icon: IconTrash,
                                    color: 'yellow.7',
                                    label: 'Delete Section'
                                }
                            ] : [], [canUpdate, props.addOptions])

        const stylingProps = useMemo(() => ({
                        compact: true,
                        rows: false,
                        darkerText: true
                    }), [])

        const onAddAction = useStableCallback((name: string) => props.onAddAction && props.onAddAction(name))

        const addButton = useMemo(() => canUpdate ? {
                        // label: 'Add Material / Service',
                        // callback: props.onAddItem,
                        customComponent: <Flex w={'100%'} gap={'md'}>
                            <Menu
                                shadow="md"
                            >
                                <Menu.Target>
                                    <Button c={'scBlue'} size={'compact-sm'} variant={'transparent'} type={'button'} >
                                        <IconPlus size={16} />
                                        <Anchor size={'sm'}>
                                            Add Line
                                        </Anchor>
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {
                                        props.addOptions.map(
                                            (x: any, i: any) => (
                                                <Menu.Item key={x.text + i} onClick={() => onAddAction(x.link)}>
                                                    {x.text} {/*{x.newOption && <NewText h={12} />}*/}
                                                </Menu.Item>
                                            )
                                        )
                                    }
                                </Menu.Dropdown>
                            </Menu>
                        </Flex>
                    } : undefined, [canUpdate, props.addOptions, onAddAction])

        return <>
            <Box pos={'relative'}>
                {/*<Box
                pos={'absolute'}
                top={-42}
            >
                <Alert color={'scBlue'} variant={'transparent'} icon={<Text c={'goldenrod'} fw={600} size={'10px'}>NEW</Text>}>
                    <Flex align={'center'} gap={5}><Text size={'sm'} mt={2} >Try our new Sections and Bundles when adding items below</Text></Flex>
                </Alert>
            </Box>*/}

                <SectionTable
                    customChildren={props.customChildren}
                    userColumnConfig={props.userColumnConfig}
                    onPredictedDefaultSectionPdfSettingsChanged={props.onPredictedDefaultSectionPdfSettingsChanged}
                    showNewBundleHint
                    data={currentData}
                    savingItem={savingItem}
                    mapping={mapping}
                    controls={controls}
                    sectionControls={sectionControls}
                    onSectionItem={handleSectionAction}
                    onDataUpdate={commit}
                    onAction={handleItemAction}
                    canEdit={canUpdate}
                    stylingProps={stylingProps}
                    onInputChange={updateField}
                    addButton={addButton}
                    height={'100%'}
                    sectionTitleKey={'InventorySectionName'}
                    sectionIdKey={'InventorySectionID'}
                    itemId={props.itemId}
                    module={Enums.Module.Quote}
                    useHideLineItemsPdfSetting
                    useDisplaySubtotalsPdfSetting
                />

            </Box>

            {showImage &&
                <SCModal
                    open={!!showImage}
                    showClose={true}
                    withCloseButton={true}
                    onClose={() => setShowImage(undefined)}
                    size={"auto"}>
                    <ImageWithZoom
                        attachment={{ ContentType: "image/jpeg", Url: showImage.Inventory?.ImageUrl ?? "", UrlThumb: showImage.Inventory?.ThumbnailUrl ?? "" }}
                    />
                </SCModal>
            }

        </>
    }
export default QuotesSectionTable
